import classNames from 'classnames'
import {Shape} from 'quickstart/components/layout/Shape'
import {falsish} from 'quickstart/types'
import {ComponentProps} from 'react'
import {IconType} from 'react-icons'
import {
  FiArrowDown,
  FiArrowLeft,
  FiArrowRight,
  FiArrowUp,
  FiChevronDown,
  FiExternalLink,
  FiHome,
  FiInfo,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from 'react-icons/fi'
import {logger} from 'tizra'
import S from './styles.module.css'

const log = logger('Icon')

const iconConfigs = {
  arrowDown: {Fi: FiArrowDown, title: 'Arrow Down'},
  arrowLeft: {Fi: FiArrowLeft, title: 'Arrow Left'},
  arrowRight: {Fi: FiArrowRight, title: 'Arrow Right'},
  arrowUp: {Fi: FiArrowUp, title: 'Arrow Up'},
  burger: {Fi: FiMenu, title: 'Menu'},
  cart: {Fi: FiShoppingBag, title: 'Shopping Cart'},
  chevronDown: {Fi: FiChevronDown, title: 'Chevron Down'},
  close: {Fi: FiX, title: 'Close'},
  externalLink: {Fi: FiExternalLink, title: 'External Link'},
  home: {Fi: FiHome, title: 'Home'},
  info: {Fi: FiInfo, title: 'Info'},
  search: {Fi: FiSearch, title: 'Search'},
  user: {Fi: FiUser, title: 'User'},
} as const

type Names = keyof typeof iconConfigs
type Configs = (typeof iconConfigs)[Names]

export const icons = Object.fromEntries(
  Object.entries(iconConfigs).map(
    ([name, {Fa, Fi, ...rest}]: [string, Configs & {Fa?: never}]) => [
      name,
      {
        fill: !!Fa, // Awesome wants, Feather doesn't
        stroke: true,
        InnerIcon: Fa || Fi,
        ...rest,
      },
    ],
  ),
) as {
  [K in Names]: {
    title: string
    stroke: boolean
    fill: boolean
    InnerIcon: Configs['Fi']
  }
}

export interface IconProps extends ComponentProps<IconType> {
  icon: keyof typeof icons
  notify?: boolean
}

const NotificationBubble = (props: ComponentProps<typeof Shape>) => (
  <Shape
    shape="circle"
    {...props}
    className={classNames(S['notification-bubble'], props.className)}
  />
)

export const Icon = ({
  icon,
  color = 'currentColor',
  notify = false,
  size = '1em',
  ...props
}: IconProps) => {
  const {InnerIcon, stroke, fill, title} = icons[icon] || {}

  if (!InnerIcon) {
    log.error('unknown icon: %s', icon)
    return null
  }

  return (
    <div className={S.wrapper} data-icon-wrapper style={{height: size}}>
      <InnerIcon
        title={title}
        {...props}
        className={classNames(S.icon, props.className)}
        style={{
          // N.B. fill:false seems to render as style="fill:" on server
          // and style="" on client, resulting in a hydration mismatch. Avoid
          // this by checking falsish.
          stroke:
            stroke === true ? color
            : falsish(stroke) ? undefined
            : stroke,
          strokeOpacity: stroke === false ? '0' : undefined,
          fill:
            fill === true ? color
            : falsish(fill) ? undefined
            : fill,
          fillOpacity: fill === false ? '0' : undefined,
          ...props.style,
        }}
      />
      {notify && <NotificationBubble />}
    </div>
  )
}
